import {
  Form,
  Collapse,
  Select,
  Input,
  DatePicker,
  ConfigProvider,
  Alert,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import UploadImage from "./UpladImage";
import { contactType } from "../constant";
import { useSelector } from "react-redux";
import { salutationsSelector, websiteIdsSelector } from "appredux/selectors";
import { ONLY_FOR_CRMKLEDO } from "utils/static";
import {
  NAME_MAX,
  NAME_ALLOWED_RE,
  COMPANY_ALLOWED_RE,
  stripName,
  stripCompany,
  isAllUpper,
  isAllLower,
  hasForbiddenSalutation,
  hasLetter,
  isSameText,
  isInhouseCompany,
} from "utils/contactValidation";
import { classNames, filterOption } from "utils/helper";
import { useContactGroup } from "queries/contact";
import DropdownItemAction from "components/UI/SearchInput/DropdownItemAction";
import { useTranslation } from "react-i18next";
import AddForm from "../Group/AddForm";
import { Link } from "react-router";
import "./style.css";
import CountrySelect from "./Address/CountrySelector";
import ProvinceSelector from "./Address/ProvinceSelector";
import CitySelector from "./Address/CitySelector";

const { TextArea } = Input;

// Aturan validasi nama/perusahaan internal berada di utils/contactValidation
// (dipakai bersama dengan gate pemilihan kontak di Step 4 tambah jadwal).
// allowUpper(v) true → nilai boleh huruf besar semua (mis. perusahaan inhouse).
const caseRules = (upperMsg, lowerMsg, allowUpper) => [
  {
    validator: (_, v) =>
      isAllUpper(v) && !allowUpper?.(v)
        ? Promise.reject(new Error(upperMsg))
        : Promise.resolve(),
  },
  {
    validator: (_, v) =>
      isAllLower(v)
        ? Promise.reject(new Error(lowerMsg))
        : Promise.resolve(),
  },
];

const ContactForm = ({
  isAdd = true,
  id,
  form,
  meetingMode = false,
  inhouseCompanies,
}) => {
  const { t } = useTranslation();
  const websiteId = useSelector(websiteIdsSelector);
  const isInternal = ONLY_FOR_CRMKLEDO.includes(websiteId);
  const nameRef = useRef(null);
  const [showPhoto, setShowPhoto] = useState(false);

  // Fokuskan ke input Nama saat dipakai dari flow tambah jadwal.
  useEffect(() => {
    if (!meetingMode) return undefined;
    const tid = setTimeout(() => nameRef.current?.focus(), 150);
    return () => clearTimeout(tid);
  }, [meetingMode]);
  const [showMore, setShowMore] = useState(false);
  const [isOpenAddGroup, setIsOpenAddGroup] = useState(false);
  const [photoFromData, setPhotoFromData] = useState(true);

  const {
    data: contactGroupData,
    isLoading,
    refetch,
  } = useContactGroup({
    search: "",
  });

  const isAsEmployee = false; //contact?.is_as_employee

  const renderCollapseUploadPhotoIcon = ({ isActive }) => {
    if (isActive) {
      return (
        <MinusOutlined
          className={isAsEmployee ? "text-secondary" : "text-primary"}
        />
      );
    }
    return (
      <PlusOutlined
        className={isAsEmployee ? "text-secondary" : "text-primary"}
      />
    );
  };

  const toggleShowPhotoWithPanel = (collapsed) => {
    setShowPhoto(!!collapsed?.length > 0);
  };
  const photoHandler = (url) => {
    setPhotoFromData(false);
    form.setFieldsValue({
      photo: url,
    });
  };
  const collapsePhotoItems = [
    {
      key: "1",
      label: (
        <span
          className={classNames(
            isAsEmployee ? "text-secondary" : "text-primary",
            "hover:underline"
          )}
          style={{
            cursor: isAsEmployee ? "not-allowed" : "cursor",
            marginLeft: -5,
          }}
          id="show-photo"
        >
          {showPhoto ? t("hide_photo") : t("show_photo")}
        </span>
      ),
      children: (
        <Form.Item label={t("photo")} name="photo">
          <UploadImage
            photoHandler={photoHandler}
            imageUrlForm={form.getFieldValue("photo")}
            id={id}
            photoFromData={photoFromData}
          />
        </Form.Item>
      ),
    },
  ];

  const groupOptions = useMemo(() => {
    if (!contactGroupData?.length) return [];

    return contactGroupData.map((v) => ({
      label: v.name,
      value: v.id,
      key: v.id,
    }));
  }, [contactGroupData]);

  const contactTypeOptions = contactType.slice(1).map((item) => {
    return {
      key: item.id,
      value: item.value,
      label: t(item.name),
    };
  });

  const salutationList = useSelector(salutationsSelector) || [];
  const salutationOptions = [...salutationList]
    // Sapaan "Kak" diutamakan di paling atas; sisanya urutan asli.
    .sort((a, b) => {
      const aKak = a.salutation?.toLowerCase() === "kak";
      const bKak = b.salutation?.toLowerCase() === "kak";
      return aKak === bKak ? 0 : aKak ? -1 : 1;
    })
    .map((salutation) => ({
      label: salutation.salutation,
      value: salutation.id,
    }));

  const onHideAddGroup = () => {
    setIsOpenAddGroup(false);
  };

  const addGroupHandler = () => {
    setIsOpenAddGroup(true);
  };
  const callbackHandler = (response) => {
    if (response.data.data.id) {
      form.setFieldsValue({
        group_id: response.data.data.id,
      });
    }
  };

  const toggleShowMore = (collapsed) => {
    setShowMore(!!collapsed?.length > 0);
  };
  // Saat meetingMode, tipe kontak & grup berada di dalam panel "Selengkapnya".
  // Paksa render agar field-nya tetap ter-mount walau panel tertutup — sehingga
  // nilai default (type_id ["3"]) ikut tervalidasi & terkirim saat submit.
  const forceRender = !isAdd || meetingMode;

  const photoCollapseEl = (
    <Collapse
      expandIcon={renderCollapseUploadPhotoIcon}
      onChange={toggleShowPhotoWithPanel}
      ghost
      collapsible={isAsEmployee ? "disabled" : "header"}
      items={collapsePhotoItems}
      className="mb-3"
    />
  );

  const typeField = (
    <Form.Item
      className="form-item-type_id "
      label={t("contact_type")}
      name="type_id"
      rules={[
        {
          required: true,
          message: t("please_select_a_contact_type_with_minimal"),
        },
      ]}
    >
      <Select
        showSearch
        placeholder={t("select_contact_type")}
        optionFilterProp="label"
        mode="multiple"
        allowClear
        filterOption={filterOption}
        options={contactTypeOptions}
      />
    </Form.Item>
  );

  const groupField = (
    <Form.Item label={t("group")} name="group_id">
      <Select
        placeholder={t("group")}
        showSearch
        allowClear
        loading={isLoading}
        options={groupOptions}
        filterOption={filterOption}
        popupRender={(menu) => (
          <DropdownItemAction
            menu={menu}
            title={t("add_group")}
            onClick={addGroupHandler}
          />
        )}
      />
    </Form.Item>
  );

  const nameRules = isInternal
    ? [
        { required: true, message: t("input_contact_name") },
        { min: 4, message: t("min_characters", { value: 4 }) },
        { max: NAME_MAX, message: t("max_characters", { value: NAME_MAX }) },
        {
          pattern: NAME_ALLOWED_RE,
          message: t("contact_validation.name_invalid_chars"),
        },
        {
          validator: (_, v) =>
            hasForbiddenSalutation(v)
              ? Promise.reject(
                  new Error(t("contact_validation.name_no_salutation"))
                )
              : Promise.resolve(),
        },
        {
          validator: (_, v) =>
            !v || hasLetter(v)
              ? Promise.resolve()
              : Promise.reject(
                  new Error(t("contact_validation.must_contain_letter"))
                ),
        },
        ...caseRules(
          t("contact_validation.name_not_all_uppercase"),
          t("contact_validation.name_not_all_lowercase")
        ),
      ]
    : [
        { required: true, message: t("input_contact_name") },
        { max: 180, message: t("max_characters", { value: 180 }) },
        { min: 2, message: t("min_characters", { value: 2 }) },
      ];

  const warningName = isInternal ? (
    <Alert
      type="warning"
      showIcon
      className="mb-3"
      message={
        <div>
          <div>{t("contact_validation.name_warning_1")}</div>
          <div>{t("contact_validation.name_warning_2")}</div>
        </div>
      }
    />
  ) : null;

  const warningCompany = isInternal ? (
    <Alert
      type="warning"
      showIcon
      className="mb-3"
      message={t("contact_validation.company_warning")}
    />
  ) : null;

  const nameFieldStandalone = (
    <div className="form-item-name">
      <Form.Item
        label={t("name")}
        name="name"
        rules={nameRules}
        getValueFromEvent={isInternal ? stripName : undefined}
      >
        <Input
          ref={nameRef}
          placeholder={t("name")}
          disabled={isAsEmployee}
          maxLength={isInternal ? NAME_MAX : undefined}
        />
      </Form.Item>
    </div>
  );

  const salutationFieldStandalone = (
    <Form.Item label={t("salutation")} name="salutation_id">
      <Select
        options={salutationOptions}
        allowClear
        placeholder={t("select_salutation")}
        disabled={isAsEmployee}
      />
    </Form.Item>
  );

  // Sapaan + Nama dalam satu baris (mode tambah jadwal), label baris = "Nama".
  const nameSalutationRow = (
    <Form.Item label={t("name")} required className="mb-3">
      <div className="flex gap-x-2">
        <Form.Item
          name="salutation_id"
          rules={[{ required: true, message: t("select_salutation") }]}
          className="mb-0 w-[110px] shrink-0"
        >
          <Select
            options={salutationOptions}
            placeholder={t("salutation")}
            disabled={isAsEmployee}
          />
        </Form.Item>
        <Form.Item
          name="name"
          rules={nameRules}
          className="mb-0 flex-1"
          getValueFromEvent={isInternal ? stripName : undefined}
        >
          <Input
            ref={nameRef}
            placeholder={t("name")}
            disabled={isAsEmployee}
            maxLength={isInternal ? NAME_MAX : undefined}
          />
        </Form.Item>
      </div>
    </Form.Item>
  );

  const phoneField = (
    <Form.Item
      label={t("phone")}
      name="phone"
      getValueFromEvent={
        meetingMode
          ? (e) => e.target.value.replace(/[^\d]/g, "")
          : undefined
      }
      rules={[
        { required: true, message: t("input_phone") },
        { max: 30, message: t("max_characters", { value: 30 }) },
        { min: 4, message: t("min_characters", { value: 4 }) },
        { pattern: /^[\d+]+$/, message: t("only_number_allowed") },
      ]}
    >
      <Input
        placeholder={t("phone")}
        disabled={isAsEmployee}
        inputMode="numeric"
      />
    </Form.Item>
  );

  const companyRules = [
    ...(meetingMode || isInternal
      ? [{ required: true, message: t("input_company") }]
      : []),
    ...(isInternal
      ? [
          { min: 4, message: t("min_characters", { value: 4 }) },
          { max: NAME_MAX, message: t("max_characters", { value: NAME_MAX }) },
          {
            pattern: COMPANY_ALLOWED_RE,
            message: t("contact_validation.company_invalid_chars"),
          },
          {
            validator: (_, v) =>
              !v || hasLetter(v)
                ? Promise.resolve()
                : Promise.reject(
                    new Error(t("contact_validation.must_contain_letter"))
                  ),
          },
          ...caseRules(
            t("contact_validation.company_not_all_uppercase"),
            t("contact_validation.company_not_all_lowercase"),
            (v) => isInhouseCompany(v, inhouseCompanies)
          ),
          {
            validator: (_, v) =>
              v && isSameText(v, form.getFieldValue("name"))
                ? Promise.reject(
                    new Error(t("contact_validation.company_same_as_name"))
                  )
                : Promise.resolve(),
          },
        ]
      : []),
  ];

  // Pengganti `dependencies={["name"]}` di field perusahaan: dependencies juga
  // memvalidasi saat field nama ter-unmount (preserve=false, mis. double mount
  // StrictMode saat modal dibuka) sehingga error perusahaan sempat berkedip.
  // Cek ulang "perusahaan sama dengan nama" hanya saat nama diubah user.
  const onValuesChange = (changedValues) => {
    if ("name" in changedValues && form.getFieldValue("company")) {
      form.validateFields(["company"]).catch(() => {});
    }
  };

  const companyField = (
    <Form.Item
      label={t("company")}
      name="company"
      rules={companyRules.length ? companyRules : undefined}
      getValueFromEvent={isInternal ? stripCompany : undefined}
    >
      <Input
        placeholder={t("company")}
        disabled={isAsEmployee}
        maxLength={isInternal ? NAME_MAX : undefined}
      />
    </Form.Item>
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            contentPadding: "0px",
            headerPadding: "0px",
          },
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_add_contact"
        initialValues={
          meetingMode ? { country_id: 1, type_id: ["3"] } : { country_id: 1 }
        }
        preserve={false}
        onValuesChange={onValuesChange}
        className="max-h-[650px] min-h-[450px] overflow-y-auto"
      >
        {!meetingMode && photoCollapseEl}
        <div className="flex flex-col">
          {meetingMode ? (
            <>
              {warningName}
              {nameSalutationRow}
              {phoneField}
              {warningCompany}
              {companyField}
              {photoCollapseEl}
            </>
          ) : (
            <>
              {typeField}
              {groupField}
              {warningName}
              {nameFieldStandalone}
              {salutationFieldStandalone}
              {phoneField}
              {warningCompany}
              {companyField}
            </>
          )}

          <Collapse
            onChange={toggleShowMore}
            ghost
            activeKey={showMore ? ["0"] : undefined}
            className="showmore-contact-collapse"
            items={[
              {
                key: "0",
                label: null,
                forceRender,
                className: "showMore",
                children: (
                  <>
                    {/* tipe kontak & grup — dipindah ke sini agar form ringkas */}
                    {meetingMode && (
                      <>
                        {typeField}
                        {groupField}
                      </>
                    )}

                    {/* address */}
                    <Form.Item
                      label={t("billing_address")}
                      name="address"
                      rules={[
                        {
                          max: 500,
                          message: t("max_characters", { value: 500 }),
                        },
                      ]}
                    >
                      <TextArea
                        placeholder={t("billing_address")}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        disabled={isAsEmployee}
                      />
                    </Form.Item>

                    {/* country */}
                    <CountrySelect label={t("country")} />

                    <div className="flex flex-row gap-x-2">
                      <div className="flex-1">
                        {/* province */}
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, next) =>
                            prev.country_id !== next.country_id
                          }
                        >
                          {({ getFieldValue, setFieldsValue }) => (
                            <>
                              {getFieldValue("country_id") === 1 && (
                                <ProvinceSelector
                                  label={t("province")}
                                  autocompleteProps={{
                                    onChange: () =>
                                      setFieldsValue({ city_id: null }),
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Form.Item>
                      </div>
                      <div className="flex-1">
                        {/* city */}
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, next) =>
                            prev.province_id !== next.province_id ||
                            prev.country_id !== next.country_id
                          }
                        >
                          {({ getFieldValue }) => (
                            <>
                              {getFieldValue("country_id") === 1 && (
                                <CitySelector
                                  label={t("city")}
                                  provinceIdSelected={getFieldValue(
                                    "province_id"
                                  )}
                                />
                              )}
                            </>
                          )}
                        </Form.Item>
                      </div>
                    </div>

                    {/* email  */}
                    <Form.Item
                      label={t("email")}
                      name="email"
                      rules={[
                        {
                          max: 100,
                          message: t("max_characters", { value: 100 }),
                        },
                        {
                          type: "email",
                          message: t("property.invalid_email_address_format"),
                        },
                      ]}
                    >
                      <Input placeholder={t("email")} disabled={isAsEmployee} />
                    </Form.Item>

                    {/* birthday  */}
                    <Form.Item label={t("date_of_birth")} name="birthday">
                      <DatePicker
                        format={"DD MMMM YYYY"}
                        style={{ width: "100%" }}
                        placeholder={t("date_of_birth")}
                        disabled={isAsEmployee}
                      />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
          <Link
            to="#"
            id="show-more-action"
            onClick={(e) => {
              e.preventDefault();
              setShowMore((prev) => !prev);
            }}
            className="text-primary hover:underline"
          >
            {showMore ? (
              <>
                <MinusOutlined style={{ color: "#007bff" }} />
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: "#007bff" }} />
              </>
            )}{" "}
            {showMore ? t("link.show_less") : t("link.show_more")}
          </Link>
        </div>
        <AddForm
          open={isOpenAddGroup}
          onCancel={onHideAddGroup}
          refetch={refetch}
          callback={callbackHandler}
        />
      </Form>
    </ConfigProvider>
  );
};

export default ContactForm;
